import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { SITE } from "@/lib/site";
import { useSectionNav } from "@/lib/scroll";
import { useSiteImages } from "@/context/SiteImagesContext";

const EASE = [0.22, 1, 0.36, 1];
const lines = ["Websites Built to", "Grow Your Business."];

export const Hero = () => {
  const goTo = useSectionNav();
  const { src } = useSiteImages();
  const heroImage = src("hero_visual");
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 20 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section id="home" className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28 lg:pt-44 lg:pb-36 grain" aria-labelledby="hero-title">
      <div className="absolute inset-0 bg-grid" aria-hidden="true" />
      <div className="orb left-1/2 -translate-x-1/2 -top-40 h-[520px] w-[820px] bg-indigo-600/20" aria-hidden="true" />

      <div className={`container-x relative grid items-center gap-16 ${heroImage ? "lg:grid-cols-[1.05fr_1fr] lg:gap-10" : "max-w-4xl text-center justify-items-center"}`} ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}>
        <div className={heroImage ? "" : "flex flex-col items-center"}>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="eyebrow mb-7">
            {SITE.tagline} · Australia
          </motion.p>

          <h1 id="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-[4.4rem] font-semibold tracking-tight leading-[1.04] text-white">
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, ease: EASE, delay: 0.15 + i * 0.14 }}
                >
                  {i === 1 ? <span className="text-gradient inline-block pr-[0.12em] -mr-[0.12em]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.55 }}
            className="mt-7 max-w-xl text-base md:text-lg text-slate-400 leading-relaxed"
          >
            Professional, modern websites designed around your business, your customers and your goals.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
            className={`mt-10 flex flex-col sm:flex-row gap-3.5 ${heroImage ? "" : "justify-center"}`}
          >
            <button onClick={() => goTo("quote")} className="btn-primary group" data-testid="hero-cta-quote-button">
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </button>
            <a href={SITE.phoneHref} className="btn-secondary" data-testid="hero-cta-phone-button">
              <Phone className="h-4 w-4" aria-hidden="true" /> Call {SITE.phone}
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className={`mt-12 flex flex-wrap gap-x-7 gap-y-2 text-sm text-slate-500 ${heroImage ? "" : "justify-center"}`}
          >
            {["Custom design", "Mobile responsive", `$${SITE.deposit} deposit · built in ${SITE.timeline}`].map((t) => (
              <li key={t} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-indigo-400 to-violet-400" aria-hidden="true" />{t}</li>
            ))}
          </motion.ul>
        </div>

        {heroImage && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.45 }}
            style={{ rotateX, rotateY, transformPerspective: 1400 }}
            className="relative lg:pl-6 will-change-transform"
          >
            <div className="relative mx-auto max-w-[620px]">
              <div className="orb -left-20 -top-16 h-72 w-72 bg-indigo-500/30 animate-pulseGlow" aria-hidden="true" />
              <div className="orb -right-10 bottom-0 h-64 w-64 bg-violet-500/25 animate-pulseGlow" aria-hidden="true" />
              <img
                src={heroImage}
                alt={`Website designed and built by ${SITE.name}`}
                className="relative w-full rounded-3xl border border-white/10 shadow-[0_50px_120px_-40px_rgba(99,102,241,0.55)]"
                fetchPriority="high"
                data-testid="hero-custom-image"
              />
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
